<template>
  <div class="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm mt-8">
    <!-- Header -->
    <div class="grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 pb-6">
      <div class="leading-none font-semibold text-lg">Operations Overview</div>
      <div class="text-muted-foreground text-sm">
        Tell us about your day-to-day operations and facilities
      </div>
    </div>

    <!-- Content -->
    <div class="px-6 space-y-6">
      <!-- Field Roster -->
      <div v-if="shouldShowMiningOperations">
        <label class="block text-sm font-medium mb-1">Field Roster</label>
        <textarea
          v-model="localData.fieldRoster"
          placeholder="Describe your typical roster arrangements (e.g., 2 weeks on / 1 week off, FIFO, residential)"
          class="w-full min-h-24 border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
          :aria-invalid="!!errors.fieldRoster"
        ></textarea>
        <p class="text-xs text-muted-foreground mt-1">
          Explain your typical work rotation and roster patterns
        </p>
        <p v-if="errors.fieldRoster" class="text-sm text-red-600 mt-1">{{ errors.fieldRoster }}</p>
      </div>

      <!-- Typical Hours -->
      <div>
        <label class="block text-sm font-medium mb-1">Typical Hours</label>
        <input
          v-model="localData.typicalHours"
          type="text"
          placeholder="12-hour shifts, 7 days per week"
          class="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
          :aria-invalid="!!errors.typicalHours"
        />
        <p class="text-xs text-muted-foreground mt-1">Standard working hours and shift patterns</p>
        <p v-if="errors.typicalHours" class="text-sm text-red-600 mt-1">{{ errors.typicalHours }}</p>
      </div>

      <!-- Facilities -->
      <div v-if="shouldShowMiningOperations">
        <label class="block text-sm font-medium mb-1">Facilities</label>
        <textarea
          v-model="localData.facilities"
          placeholder="Describe your facilities (e.g., accommodation camps, offices, processing plants, workshops)"
          class="w-full min-h-24 border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
          :aria-invalid="!!errors.facilities"
        ></textarea>
        <p class="text-xs text-muted-foreground mt-1">
          List and describe your key facilities and infrastructure
        </p>
        <p v-if="errors.facilities" class="text-sm text-red-600 mt-1">{{ errors.facilities }}</p>
      </div>

      <!-- Software Used -->
      <div>
        <div class="flex items-center gap-2 mb-1">
          <label class="block text-sm font-medium">Software Used</label>
          <span class="text-muted-foreground text-xs">(ERP, CAD, specialized tools)</span>
        </div>
        <p class="text-xs text-muted-foreground mb-2">
          Select all enterprise software and systems your company uses
        </p>
        <TagAutocomplete
          v-model="softwareSelection"
          :options="softwareOptions"
          placeholder="Search software or add your own"
          clearable
        />
      </div>
    </div>

    <!-- Footer -->
    <div class="flex justify-between items-center px-6 pt-6">
      <button
        class="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted"
        @click="prevStep"
      >
        Back
      </button>
      <button
        class="px-4 py-2 rounded-md bg-brand text-white text-sm hover:opacity-90"
        @click="validateAndProceedStep4"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue'
import { SOFTWARE_LOOKUPS } from '@/constants/lookups'
import TagAutocomplete from '@/components/inputs/TagAutocomplete.vue'
import { useCompanyRegistrationStore } from '@/stores/companyRegistrationStore'
import { useCompanyIndustryProfileStore } from '@/stores/companyIndustryProfileStore'

const props = defineProps<{
  data: Record<string, any>
}>()

const emits = defineEmits(['update', 'back', 'next'])

const localData = ref({ ...props.data })

const registrationStore = useCompanyRegistrationStore()
const industryProfileStore = useCompanyIndustryProfileStore()

const errors = reactive<{ [key: string]: string }>({})

watch(localData, (val) => emits('update', val), { deep: true })

const selectedIndustry = computed(() => {
  const industryValue = localData.value.industry
  if (!industryValue) return null
  const industryId = Number(industryValue)
  if (!Number.isFinite(industryId)) return null

  const industries = registrationStore.industries ?? []
  return industries.find((industry) => industry.id === industryId) ?? null
})

const resolvedIndustryName = computed(() => {
  if (selectedIndustry.value?.name) {
    return selectedIndustry.value.name
  }

  const raw = localData.value.industry
  if (raw == null || raw === '') return ''

  if (typeof raw === 'string') return raw.trim()
  return String(raw)
})

const shouldShowMiningOperations = computed(
  () => industryProfileStore.resolve(resolvedIndustryName.value)?.sections.site_operations ?? false
)

const industrySoftwareKey = computed(() => {
  const normalized = resolvedIndustryName.value.trim().toLowerCase()
  return normalized !== '' && normalized.includes('mining') ? 'mining' : 'default'
})

const softwareOptions = computed(() => {
  const key = industrySoftwareKey.value
  const options = SOFTWARE_LOOKUPS[key] ?? SOFTWARE_LOOKUPS.default
  return [...options]
})

const softwareSelection = computed<string[]>({
  get() {
    return Array.isArray(localData.value.softwareUsed) ? localData.value.softwareUsed : []
  },
  set(value) {
    localData.value.softwareUsed = value
  },
})

function prevStep() {
  emits('back')
}

function validateAndProceedStep4() {
  for (const k in errors) delete errors[k]

  if (Object.keys(errors).length === 0) {
    emits('next')
  }
}

watch(shouldShowMiningOperations, (show) => {
  if (show) return
  delete errors.fieldRoster
  delete errors.facilities
})
</script>
